import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import apiClient, { type ApiResponse } from '@/lib/api';

type VerificationStatus = 'loading' | 'success' | 'error';

interface VerifyEmailBody {
  success: boolean;
  message?: string;
  error?: string;
  tenantSlug?: string;
}

// The /auth/verify-email endpoint returns its payload flat (success,
// message?, error?, tenantSlug?). The shared ApiResponse<T> shape exposes
// the same `success` field plus an optional `data: T`, so reading the
// flat fields off the wrapper works for both shapes.
function readBody(response: ApiResponse<VerifyEmailBody> | VerifyEmailBody): VerifyEmailBody {
  const wrapped = response as ApiResponse<VerifyEmailBody>;
  if (wrapped && wrapped.data && typeof wrapped.data === 'object') {
    return wrapped.data;
  }
  return response as VerifyEmailBody;
}

function getErrorStatus(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'status' in error) {
    const s = (error as { status?: unknown }).status;
    if (typeof s === 'number') return s;
  }
  return undefined;
}

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const [status, setStatus] = useState<VerificationStatus>('loading');
  const [message, setMessage] = useState('');
  const [returnSlug, setReturnSlug] = useState<string | null>(null);

  const verifyEmail = useCallback(async (token: string) => {
    try {
      const response = await apiClient.get<VerifyEmailBody>(
        `/auth/verify-email?token=${encodeURIComponent(token)}`
      );
      const body = readBody(response);

      if (body.success) {
        setStatus('success');
        setMessage(body.message || 'Email verificata con successo!');
        setReturnSlug(body.tenantSlug || tenantSlug || null);
      } else {
        setStatus('error');
        setMessage(body.error || 'Errore nella verifica del link.');
      }
    } catch (error) {
      setStatus('error');
      const status = getErrorStatus(error);
      const message = error instanceof Error ? error.message : '';
      if (status && status >= 500) {
        setMessage('Servizio temporaneamente non disponibile. Riprova tra qualche secondo.');
      } else if (!status) {
        setMessage('Impossibile contattare il server. Controlla la connessione e riprova.');
      } else {
        setMessage(message || 'Errore nella verifica del link.');
      }
    }
  }, [tenantSlug]);

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('Token di verifica mancante');
      return;
    }

    verifyEmail(token);
  }, [searchParams, verifyEmail]);

  const goToLogin = () => {
    if (returnSlug) {
      navigate(`/${returnSlug}/login`);
    } else if (tenantSlug) {
      navigate(`/${tenantSlug}/login`);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
      <Card className="w-full max-w-md bg-white/10 backdrop-blur-xl border-white/20">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            {status === 'loading' && (
              <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center">
                <Loader2 className="h-8 w-8 text-blue-400 animate-spin" />
              </div>
            )}
            {status === 'success' && (
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-400" />
              </div>
            )}
            {status === 'error' && (
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
                <XCircle className="h-8 w-8 text-red-400" />
              </div>
            )}
          </div>
          <CardTitle className="text-2xl text-white">
            {status === 'loading' && 'Verifica in corso...'}
            {status === 'success' && 'Email Verificata!'}
            {status === 'error' && 'Verifica Fallita'}
          </CardTitle>
          <CardDescription className="text-gray-300">
            {message || 'Stiamo verificando il tuo indirizzo email...'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'success' && (
            <div className="text-center space-y-4">
              <p className="text-gray-300">
                Il tuo account è ora attivo. Puoi effettuare il login.
              </p>
              <Button 
                onClick={goToLogin} 
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              >
                Vai al Login
              </Button>
            </div>
          )}
          
          {status === 'error' && (
            <div className="text-center space-y-4">
              <p className="text-gray-300 text-sm">
                Se il link è scaduto, contatta l'amministratore per richiedere un nuovo link di verifica.
              </p>
              <Button 
                onClick={goToLogin} 
                variant="outline"
                className="w-full border-white/20 text-white hover:bg-white/10"
              >
                Torna al Login
              </Button>
            </div>
          )}
          
          {status === 'loading' && (
            <div className="flex items-center justify-center gap-2 text-gray-400">
              <Mail className="h-4 w-4" />
              <span>Attendere prego...</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
